import { ref } from 'vue';
import { useApi, getApiConfig } from './useApi';
export function getTransactionClassificationTree(onSuccess = null, refreshCache = false) {
    const api = useApi();
    const config = getApiConfig();
    const url = config.api + (config.useCachedEndpoints ? '/Cached' : '') + '/V2/Customers/00000000-0000-0000-0000-000000000000/SpendingAnalysis/TransactionClassificationTree';
    api.GET(url, (result) => {
        if (onSuccess)
            onSuccess(result);
    }, !refreshCache, refreshCache, /* ttlMins */ 30, /* retrying */ false, /* staleWhileRevalidate */ true);
}
export function getSpendingAnalysis(fromDate, toDate, options = {}, onSuccess = null, refreshCache = false, staleWhileRevalidate = false, pollUntilChanged = false) {
    const api = useApi();
    const config = getApiConfig();
    const params = new URLSearchParams();
    if (fromDate)
        params.set('FromDate', fromDate.toISOString());
    if (toDate)
        params.set('ToDate', toDate.toISOString());
    if (options.category)
        params.set('Category', options.category);
    if (options.subCategory)
        params.set('SubCategory', options.subCategory);
    if (options.merchant)
        params.set('Merchant', options.merchant);
    if (options.accountIds)
        options.accountIds.forEach(id => params.append('AccountIds', id));
    if (options.snapToSpendingCycle !== undefined)
        params.set('SnapToSpendingCycle', options.snapToSpendingCycle ? 'true' : 'false');
    const url = config.api + (config.useCachedEndpoints ? '/Cached' : '') + `/V2/Customers/00000000-0000-0000-0000-000000000000/SpendingAnalysis?${params.toString()}`;
    api.GET(url, (result, isStale) => {
        if (onSuccess)
            onSuccess(result, isStale);
    }, !refreshCache, refreshCache, /* ttlMins */ undefined, /* retrying */ false, staleWhileRevalidate, pollUntilChanged);
}
export function clearSpendingAnalysisCache() {
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('/V2/Customers/00000000-0000-0000-0000-000000000000/SpendingAnalysis?')) {
            localStorage.removeItem(key);
            i--;
        }
    }
}
export const SPENDING_BREAKDOWN_PALETTE = ['#66CCAA', '#413655', '#9EA2D0', '#D5C9C8', '#C6CAE5', '#B4D8D7', '#EEF0FF', '#EFE8E6'];
export function buildSpendingOverviewBreakdown(parentCategories, colourLookup) {
    const parents = Array.isArray(parentCategories) ? parentCategories : [];
    return parents.map((p, i) => {
        const isIncome = p.name === 'Income';
        const direction = isIncome ? 'in' : 'out';
        const amount = isIncome ? (p.totalIn ?? 0) : (p.totalOut ?? 0);
        return {
            name: p.name,
            amount,
            colour: colourLookup(p.name) ?? SPENDING_BREAKDOWN_PALETTE[i % SPENDING_BREAKDOWN_PALETTE.length],
            direction,
        };
    }).filter((b) => b.amount > 0);
}
export function buildCategoryRows(summary, isAllView, selectedSubCategory, selectedSubCategoryColour) {
    if (isAllView) {
        // Aggregate every parent category's transactions by sub-classification so the
        // All view lists sub-categories rather than top-level parents.
        const parents = summary?.parentCategories ?? [];
        if (!Array.isArray(parents))
            return [];
        const subMap = new Map();
        for (const p of parents) {
            const txs = Array.isArray(p.transactions) ? p.transactions : [];
            for (const t of txs) {
                const name = t.subClassification ?? 'Uncategorised';
                let row = subMap.get(name);
                if (!row) {
                    row = { name, totalIn: 0, totalOut: 0, transactionCount: 0, colour: t.color ?? p.categoryColor ?? p.color ?? '', iconPath: t.icon ?? undefined };
                    subMap.set(name, row);
                }
                else if (!row.iconPath && t.icon) {
                    row.iconPath = t.icon;
                }
                if (t.amount >= 0)
                    row.totalIn += t.amount;
                else
                    row.totalOut += -t.amount;
                row.transactionCount += 1;
            }
        }
        const rows = [...subMap.values()].map((r, i) => {
            const amount = r.totalOut > r.totalIn ? r.totalOut : r.totalIn;
            return {
                name: r.name,
                transactionCount: r.transactionCount,
                amount: Math.abs(amount),
                colour: r.colour || SPENDING_BREAKDOWN_PALETTE[i % SPENDING_BREAKDOWN_PALETTE.length],
                iconPath: r.iconPath,
                isIncome: r.totalIn > r.totalOut,
            };
        });
        return rows.filter(r => r.amount > 0).sort((a, b) => b.amount - a.amount);
    }
    const source = summary?.categories ?? [];
    if (!Array.isArray(source))
        return [];
    const rows = source.map((c, i) => {
        const totalIn = c.totalIn ?? 0;
        const totalOut = c.totalOut ?? 0;
        const amount = totalOut > totalIn ? totalOut : totalIn;
        const fallbackColour = selectedSubCategory ? (selectedSubCategoryColour || SPENDING_BREAKDOWN_PALETTE[i % SPENDING_BREAKDOWN_PALETTE.length]) : SPENDING_BREAKDOWN_PALETTE[i % SPENDING_BREAKDOWN_PALETTE.length];
        const iconPath = c.iconPath ?? c.icon ?? (Array.isArray(c.transactions) ? c.transactions[0]?.icon : undefined);
        const txColour = Array.isArray(c.transactions) ? c.transactions[0]?.color : undefined;
        return {
            name: c.subCategory || c.name,
            transactionCount: Array.isArray(c.transactions) ? c.transactions.length : 0,
            amount: Math.abs(amount),
            colour: txColour ?? c.color ?? fallbackColour,
            iconPath,
            isIncome: totalIn > totalOut,
        };
    });
    return rows.filter(r => r.amount > 0).sort((a, b) => b.amount - a.amount);
}
// Sort transactions newest-first, then group by calendar day (YYYY-MM-DD) so two
// items on the same date land in the same group across years/months.
export function groupTransactionsByDay(flat) {
    const sorted = [...flat].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    const byDay = new Map();
    for (const tx of sorted) {
        const key = String(tx.timestamp).slice(0, 10);
        if (!byDay.has(key))
            byDay.set(key, []);
        byDay.get(key).push(tx);
    }
    return Array.from(byDay.values());
}
// Index of the bar covering "now", so a chart can highlight the current month.
export function findCurrentMonthIndex(bars) {
    const now = Date.now();
    const idx = bars.findIndex(b => {
        const from = b.fromDate ? new Date(b.fromDate).getTime() : 0;
        const to = b.toDate ? new Date(b.toDate).getTime() : 0;
        return from <= now && now < to;
    });
    return idx >= 0 ? idx : undefined;
}
// Must match the format used for `state.today` / `state.yesterday` in useAppState
// — TransactionsList compares them by string to render "Today" / "Yesterday".
export function formatTimestampString(timestamp) {
    const d = new Date(timestamp);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}
// Flattens spending-analysis category/parent-category transactions into a deduped,
// date-grouped list shaped for TransactionsList / RecentTransactionsSection.
export function buildTransactionGroups(summary) {
    if (!summary)
        return [];
    const source = (Array.isArray(summary.parentCategories) && summary.parentCategories.length > 0)
        ? summary.parentCategories
        : (Array.isArray(summary.categories) ? summary.categories : []);
    const flat = [];
    const seen = new Set();
    for (const c of source) {
        if (!Array.isArray(c.transactions))
            continue;
        for (const t of c.transactions) {
            const id = t.id ?? `${t.merchantName}-${t.timestamp}-${t.amount}`;
            if (seen.has(id))
                continue;
            seen.add(id);
            flat.push({
                id,
                merchantName: t.merchantName,
                merchantID: t.merchantID,
                amount: t.amount,
                timestamp: t.timestamp,
                timestampString: formatTimestampString(t.timestamp),
                wellKnownMerchant: t.wellKnownMerchant,
                customerTags: t.tags ?? t.customerTags,
                effectiveClassification: {
                    mgClassification: t.classification,
                    mgSubClassification: t.subClassification,
                    color: c.color ?? t.color,
                    iconPath: t.icon,
                },
            });
        }
    }
    return groupTransactionsByDay(flat);
}
// Derive the effective [startMonth, endMonth] window for the monthly chart
// based on the active date range (if any) or the selected period preset.
// Returns null when the caller should fall back to the default month window
// (e.g. the `month` preset, since that feeds the weekly view instead).
export function getMonthRangeForSelection(selection, period, today = new Date()) {
    if (selection) {
        if (selection.type === 'months') {
            if (!selection.startMonth || !selection.endMonth)
                return null;
            return { startMonth: selection.startMonth, endMonth: selection.endMonth };
        }
        if (selection.type === 'calendarYear' && selection.year) {
            const y = selection.year;
            return { startMonth: `${y}-01`, endMonth: `${y}-12` };
        }
        if (selection.type === 'taxYear' && selection.year) {
            const y = selection.year;
            return { startMonth: `${y}-04`, endMonth: `${y + 1}-03` };
        }
        return null;
    }
    const y = today.getFullYear();
    if (period === 'Year') {
        return { startMonth: `${y}-01`, endMonth: `${y}-12` };
    }
    if (period === 'tax') {
        // UK tax year runs April–March. If we're Jan–Mar we're still in the
        // previous April's tax year.
        const m = today.getMonth(); // 0-indexed
        if (m >= 3)
            return { startMonth: `${y}-04`, endMonth: `${y + 1}-03` };
        return { startMonth: `${y - 1}-04`, endMonth: `${y}-03` };
    }
    return null;
}
// Date range for the current-month default view. When snapping to the spending
// cycle we pass no dates and let the backend pick the window; otherwise we pass
// explicit 1st-of-this-month → 1st-of-next-month calendar-month dates.
export function getDefaultMonthRange(snapToSpendingCycle, today = new Date()) {
    if (snapToSpendingCycle)
        return { fromDate: null, toDate: null };
    return {
        fromDate: new Date(Date.UTC(today.getFullYear(), today.getMonth(), 1)),
        toDate: new Date(Date.UTC(today.getFullYear(), today.getMonth() + 1, 1)),
    };
}
export const GRANDPARENT_CLASSIFICATIONS = {
    INCOME: 0,
    FUTURE_PLANNING: 1,
    EVERYDAY_SPEND: 2,
    FIXED_COSTS: 3
};
export function useSpendingAnalysis() {
    const breadCrumb = ref([]);
    const showMerchantTransactions = ref(false);
    const showPlanManagement = ref(false);
    const selectedClassificationSpend = ref(0);
    const selectedClassificationIncome = ref(0);
    const categoryWellData = ref([]);
    function drawerTitle(merchantTransactions, selectedClassification) {
        if (showMerchantTransactions.value) {
            return merchantTransactions.length > 0 ? merchantTransactions[0][0].merchantName : '';
        }
        return selectedClassification ? selectedClassification.fullName : '';
    }
    function findClassificationByName(name, classifications, returnParent = false) {
        for (const classification of classifications) {
            if (classification.mgClassification.mgClassification == name) {
                return classification;
            }
            if (classification.children && classification.children.length > 0) {
                const foundClassification = findClassificationByName(name, classification.children);
                if (foundClassification) {
                    if (returnParent) {
                        return classification;
                    }
                    return foundClassification;
                }
            }
        }
        return null;
    }
    function findClassificationById(id, classifications, returnParent = false) {
        for (const classification of classifications) {
            if (classification.mgClassification.id === id) {
                return classification;
            }
            if (classification.children && classification.children.length > 0) {
                const foundClassification = findClassificationById(id, classification.children);
                if (foundClassification) {
                    if (returnParent) {
                        return classification;
                    }
                    return foundClassification;
                }
            }
        }
        return null;
    }
    function findClassificationsByGrandparentClassification(grandParentClassification, classifications) {
        const result = [];
        for (const classification of classifications) {
            if (classification.mgClassification.grandParentClassification === grandParentClassification) {
                result.push(classification);
            }
            if (classification.children && classification.children.length > 0) {
                const foundClassifications = findClassificationsByGrandparentClassification(grandParentClassification, classification.children);
                if (foundClassifications) {
                    result.push(...foundClassifications);
                }
            }
        }
        return result;
    }
    function navigateToPlan(classification, spendingPlansFull, createPlanFn, options = {}) {
        showPlanManagement.value = true;
        if (options.closeDrawerNext)
            options.closeDrawerNext.value = false;
        let plan = spendingPlansFull.plans.find((p) => p.classification.id === classification.id);
        if (!plan || !plan.plan) {
            plan = {
                classification: classification,
                plan: {
                    amount: 0
                },
                avgNetSpend: 0,
                avgTransactionCount: 0
            };
            createPlanFn(plan);
            if (options.resetSections) {
                options.resetSections();
            }
        }
        else {
            createPlanFn(plan);
            if (options.setFieldValue && plan.plan) {
                options.setFieldValue('Plan amount', plan.plan.amount);
            }
            if (options.sections && options.sections.length > 0 && options.sections[0].fields) {
                options.sections.splice(0, options.sections.length, {
                    fields: [
                        {
                            label: 'Plan amount',
                            type: 'currency',
                            value: plan.plan ? plan.plan.amount : null,
                            required: true
                        }
                    ]
                });
            }
        }
    }
    function handleDrawerClosed(context) {
        context.merchantTransactions.value = [];
        categoryWellData.value = [];
        context.selectedClassification.value = null;
        showMerchantTransactions.value = false;
        context.selectedClassificationPlanAmount.value = 0;
        showPlanManagement.value = false;
    }
    function handleMerchantClicked(merchant, transactions, context) {
        context.merchantTransactions.value = [];
        const txns = transactions.transactions.filter((transaction) => transaction.merchantName === merchant.label);
        // Group transactions by timestampString
        const groupedByTimestamp = txns.reduce((acc, transaction) => {
            const timestamp = transaction.timestampString || 'Unknown';
            if (!acc[timestamp]) {
                acc[timestamp] = [];
            }
            acc[timestamp].push(transaction);
            return acc;
        }, {});
        // Convert grouped transactions to an array of arrays
        context.merchantTransactions.value = Object.keys(groupedByTimestamp).map(timestamp => groupedByTimestamp[timestamp]);
        context.selectedMerchantSpend.value = context.merchantTransactions.value.reduce((total, transactions) => {
            return total + transactions.reduce((subTotal, transaction) => subTotal + transaction.amount, 0);
        }, 0);
        showMerchantTransactions.value = true;
        showPlanManagement.value = false;
        context.closeDrawerNext.value = false;
    }
    function handleCategoryClicked(classification, grandParentClassification, context) {
        const selectedSlot = context.slots.find((s) => s.key === context.selectedSlotKey);
        context.getTransactions(classification.id, selectedSlot.from, selectedSlot.to, (transactions) => {
            const grouped = transactions.transactions.reduce((acc, transaction) => {
                const merchantName = transaction.merchantName || 'Unknown';
                if (!acc[merchantName]) {
                    acc[merchantName] = {
                        merchantName: merchantName,
                        totalAmount: 0,
                        transactions: []
                    };
                }
                acc[merchantName].totalAmount += transaction.amount;
                acc[merchantName].transactions.push(transaction);
                return acc;
            }, {});
            context.groupedTransactions.value = grouped;
            categoryWellData.value = [];
            if (Object.keys(grouped).length > 0) {
                Object.keys(grouped).forEach((merchantName, index) => {
                    const count = grouped[merchantName].transactions.length;
                    const totalamount = grouped[merchantName].transactions.reduce((total, transaction) => {
                        return total + transaction.amount;
                    }, 0);
                    categoryWellData.value.push({
                        order: index + 1,
                        label: merchantName,
                        value: context.formatter.format(Math.abs(Math.round(totalamount))),
                        supplement: (count > 1 ? ' (x' + count + ')' : ''),
                        moneyIn: totalamount > 0,
                    });
                });
            }
            else {
                categoryWellData.value.push({
                    label: 'No data',
                    value: 0,
                    supplement: '',
                    moneyIn: false
                });
            }
            // Calculate totals from the same transactions used for the merchant breakdown
            selectedClassificationSpend.value = transactions.transactions.reduce((total, t) => total + t.amount, 0);
            selectedClassificationIncome.value = transactions.transactions.filter((t) => t.amount > 0).reduce((total, t) => total + t.amount, 0);
        }, /* includeChildrenClassifications */ true, /* refreshCache */ false, /* includeExcludedFromAnalysis */ false);
        const selectedClassificationPlan = context.spendingPlans.plans.find((plan) => plan.classification.id === classification.id);
        if (selectedClassificationPlan) {
            context.selectedClassificationPlanAmount.value = selectedClassificationPlan.plan.amount;
        }
        context.selectedClassification.value = classification;
        const classificationColours = {
            [GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND]: '#EEF0FF',
            [GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING]: '#EFE8E6',
            [GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS]: '#413655',
            [GRANDPARENT_CLASSIFICATIONS.INCOME]: '#B4D8D7'
        };
        context.selectedClassification.value.bgColour = classificationColours[context.selectedClassification.value.grandParentClassification];
        context.transactionDrawer.openDrawer();
    }
    function selectedCategory(categoryArray, grandparentClassification, selectedSlotKey, spendingPlans) {
        let selected = categoryArray.find((s) => s.slotName === selectedSlotKey);
        if (!selected) {
            selected = {
                label: selectedSlotKey,
                totalIn: 0,
                totalOut: 0,
                totalNet: 0,
                groupedAnalysis: []
            };
        }
        // add any missing groups that we have plans for
        const newGroups = [];
        const plans = spendingPlans.plans.filter((plan) => plan.classification.grandParentClassification === grandparentClassification);
        plans.forEach((plan) => {
            // if this plan isn't found in the analysis, add it
            if (!selected.groupedAnalysis.find((g) => g.name === plan.classification.fullName)) {
                newGroups.push({
                    name: plan.classification.fullName,
                    transactionClassification: plan.classification,
                    amountIn: 0,
                    amountOut: 0,
                    amountNet: 0,
                });
            }
        });
        selected.groupedAnalysis = selected.groupedAnalysis.concat(newGroups);
        // sort the groups by amountOut (abs)
        selected.groupedAnalysis.sort((a, b) => Math.abs(b.amountNet) - Math.abs(a.amountNet));
        // If the groupedAnalysis.name is 'Uncategorised' then change then name to: 'Tag n transactions'.  Get n from groupedAnalysis.countNegativeTransactions
        selected.groupedAnalysis.forEach((group) => {
            if (group.name === 'Uncategorized') {
                group.name = 'Tag ' + group.countNegativeTransactions + ' transaction' + (group.countNegativeTransactions !== 1 ? 's' : '');
            }
        });
        return selected;
    }
    return {
        breadCrumb,
        showMerchantTransactions,
        showPlanManagement,
        selectedClassificationSpend,
        selectedClassificationIncome,
        categoryWellData,
        drawerTitle,
        findClassificationByName,
        findClassificationById,
        findClassificationsByGrandparentClassification,
        navigateToPlan,
        handleDrawerClosed,
        handleMerchantClicked,
        handleCategoryClicked,
        selectedCategory,
    };
}
